// ===================================================
// GAM Command Center — custom order for the תוספים panel
// ===================================================
//
// Gal's ask: inside the slash panel, the pencil should let him reorder the
// plugin FAMILIES (categories) and the plugins WITHIN a family.
//
// Stored in localStorage, like every other sidebar preference — a per-browser
// display choice, not shared data, so nothing here touches the database.
//
// The stored list is a HINT, never the source of truth. The live catalog can
// gain a plugin, lose one, or rename a family between two visits; anything the
// stored list doesn't mention keeps its catalog position at the end, and
// anything it mentions that no longer exists is ignored. That way a stale
// preference degrades into "mostly your order" instead of a blank menu.

const ORDER_KEY = 'gam-slash-order';

export interface SlashOrder {
  /** Category names (the English `name`), in the order the user chose. */
  categories: string[];
  /** category name → item titles (the English `title`), in the chosen order. */
  items: Record<string, string[]>;
}

export const EMPTY_SLASH_ORDER: SlashOrder = { categories: [], items: {} };

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

export function readSlashOrder(): SlashOrder {
  if (typeof window === 'undefined') return EMPTY_SLASH_ORDER;
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (!raw) return EMPTY_SLASH_ORDER;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY_SLASH_ORDER;
    const obj = parsed as Record<string, unknown>;
    const categories = isStringArray(obj.categories) ? obj.categories : [];
    const items: Record<string, string[]> = {};
    if (obj.items && typeof obj.items === 'object') {
      for (const [key, value] of Object.entries(obj.items as Record<string, unknown>)) {
        if (isStringArray(value)) items[key] = value;
      }
    }
    return { categories, items };
  } catch {
    return EMPTY_SLASH_ORDER;
  }
}

export function writeSlashOrder(order: SlashOrder): SlashOrder {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    } catch {
      /* quota / private mode — the menu still works, it just won't remember */
    }
  }
  return order;
}

export function resetSlashOrder(): SlashOrder {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(ORDER_KEY);
    } catch {
      /* ignore */
    }
  }
  return EMPTY_SLASH_ORDER;
}

/**
 * Sort `live` by the position of `key(entry)` in `wanted`. Entries `wanted`
 * doesn't mention keep their catalog order and land after the ones it does.
 */
function sortByWanted<T>(live: T[], key: (entry: T) => string, wanted: string[]): T[] {
  if (wanted.length === 0) return live;
  const rank = new Map(wanted.map((name, i) => [name, i]));
  return live
    .map((entry, i) => ({ entry, i, r: rank.get(key(entry)) ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => (a.r !== b.r ? a.r - b.r : a.i - b.i))
    .map((x) => x.entry);
}

export function orderCategories<T extends { name: string }>(cats: T[], order: SlashOrder): T[] {
  return sortByWanted(cats, (c) => c.name, order.categories);
}

export function orderItems<T extends { title: string }>(
  categoryName: string,
  items: T[],
  order: SlashOrder
): T[] {
  return sortByWanted(items, (i) => i.title, order.items[categoryName] ?? []);
}

/** Swap `key` with its neighbour. `dir` is -1 for up, +1 for down. */
function swap(list: string[], key: string, dir: -1 | 1): string[] {
  const from = list.indexOf(key);
  const to = from + dir;
  if (from < 0 || to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/**
 * Move a family one slot. `visible` is the list AS CURRENTLY RENDERED — we
 * rewrite the stored order from what the user is actually looking at, so a
 * stale entry can never make the arrow move something off-screen.
 */
export function moveCategory(order: SlashOrder, visible: string[], name: string, dir: -1 | 1): SlashOrder {
  return { ...order, categories: swap(visible, name, dir) };
}

/** Same, for one plugin inside its family. */
export function moveItem(
  order: SlashOrder,
  categoryName: string,
  visible: string[],
  title: string,
  dir: -1 | 1
): SlashOrder {
  return {
    ...order,
    items: { ...order.items, [categoryName]: swap(visible, title, dir) },
  };
}
